import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { CreditCard, Smartphone } from 'lucide-react'
import { usePayment } from '../../contexts/PaymentContext'
import { formatPrice } from '../../lib/square'
import { supabase } from '../../lib/supabase'
import LoadingSpinner from '../ui/LoadingSpinner'
import TipSelector from '../checkout/TipSelector'

interface PaymentFormProps {
  amount: number
  reservationId: string | null // If provided, converts reservation to order instead of calling placeOrder
  placeOrder: () => Promise<string> // Returns orderId (legacy fallback)
  onSuccess: (orderId: string, paymentResult: any) => void
  onError: (error: string) => void
  // Tax & Tip: Breakdown for payment processing
  subtotal?: number
  taxRate?: number
  taxAmount?: number
  tipAmount?: number
  onTipChange?: (amount: number) => void
}

const PaymentForm: React.FC<PaymentFormProps> = ({
  amount,
  reservationId,
  placeOrder,
  onSuccess,
  onError,
  subtotal,
  taxRate,
  taxAmount,
  tipAmount,
  onTipChange
}) => {
  const {
    initializePayment,
    processCardPayment,
    processApplePayment,
    processGooglePayment,
    isProcessing,
    paymentError,
    clearError,
    isApplePayAvailable,
    isGooglePayAvailable
  } = usePayment()

  const [isInitialized, setIsInitialized] = useState(false)
  const [selectedMethod, setSelectedMethod] = useState<'card' | 'apple' | 'google'>('card')
  const [agreedToTerms, setAgreedToTerms] = useState(false)

  // Initialize payment form on component mount
  useEffect(() => {
    let isMounted = true

    // Set initialized immediately so the DOM renders with #card-container
    setIsInitialized(true)

    // Then initialize Square SDK after a small delay to ensure DOM is painted
    const timeoutId = setTimeout(() => {
      if (!isMounted) return

      initializePayment('card-container', amount)
        .catch(error => {
          console.error('Payment initialization error:', error)
          if (isMounted) {
            onError('Failed to load payment form')
            setIsInitialized(false)
          }
        })
    }, 100)

    return () => {
      isMounted = false
      clearTimeout(timeoutId)
    }
  }, [amount, initializePayment, onError])

  // Clear errors when payment method changes
  useEffect(() => {
    clearError()
  }, [selectedMethod, clearError])

  // Handle payment submission with order creation
  const handlePayment = async () => {
    try {
      clearError()

      // Validate Terms of Sale agreement
      if (!agreedToTerms) {
        onError('You must agree to the Terms of Sale before completing your purchase.')
        return
      }

      // Step 1: Create order (either convert reservation or legacy placeOrder)
      let orderId: string
      try {
        if (reservationId) {
          // NEW PATH: Convert reservation to order
          const { data: { session } } = await supabase.auth.getSession()
          if (!session?.access_token) {
            throw new Error('No active session')
          }

          const response = await fetch('/.netlify/functions/convert-reservation-to-order', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${session.access_token}`
            },
            body: JSON.stringify({
              reservationId,
              subtotalAmount: subtotal,
              taxRate: taxRate,
              taxAmount: taxAmount,
              tipAmount: tipAmount,
              totalAmount: amount / 100 // Convert cents to dollars
            })
          })

          const data = await response.json()

          if (!response.ok) {
            // Handle specific reservation errors
            if (data.code === 'RESERVATION_EXPIRED') {
              throw new Error('Your reservation has expired. Please return to the event to place a new order.')
            }
            if (data.code === 'RESERVATION_NOT_FOUND') {
              throw new Error('Reservation not found or already used.')
            }
            if (data.code === 'CAPACITY_EXCEEDED') {
              throw new Error('This event is now sold out.')
            }
            throw new Error(data.error || 'Failed to convert reservation to order')
          }

          orderId = data.orderId
        } else {
          // LEGACY PATH: Direct order creation (for backwards compatibility)
          orderId = await placeOrder()
        }
      } catch (orderError: any) {
        // Order creation failed (capacity exceeded, validation error, etc.)
        onError(orderError.message || 'Unable to place order. Please try again.')
        return
      }

      // Step 2: Process payment with Square
      let result
      try {
        switch (selectedMethod) {
          case 'apple':
            result = await processApplePayment(amount, orderId)
            break
          case 'google':
            result = await processGooglePayment(amount, orderId)
            break
          default:
            result = await processCardPayment(amount, orderId)
        }
      } catch (paymentError: any) {
        // Payment failed - order remains in 'pending' state (will be cleaned up by cron)
        onError(paymentError.message || 'Payment failed')
        return
      }

      // Step 3: Handle payment result
      if (result.errors) {
        onError(paymentError || 'Payment processing failed')
      } else {
        onSuccess(orderId, result)
      }
    } catch (error: any) {
      onError(error.message || 'An unexpected error occurred')
    }
  }

  if (!isInitialized) {
    return (
      <div className="flex items-center justify-center p-8">
        <LoadingSpinner size="md" />
        <span className="ml-2 text-muted-foreground">Loading payment form...</span>
      </div>
    )
  }

  return (
    <div>
      {/* Tip Selection - shown at top of payment step */}
      {subtotal !== undefined && onTipChange && (
        <TipSelector
          subtotal={subtotal}
          currentTip={tipAmount || 0}
          onTipChange={onTipChange}
        />
      )}

      {/* Payment Amount */}
      <div className="bg-gray-50 dark:bg-gray-800 p-6 rounded-lg border-2 border-maroon-800 mb-6">
        <p className="text-sm font-medium text-gray-500 dark:text-gray-400 text-center">TOTAL AMOUNT</p>
        <p className="text-4xl font-extrabold text-gray-900 dark:text-white text-center mt-1">{formatPrice(amount)}</p>
      </div>

      {/* Payment Method Selection */}
      <div className="space-y-3 mb-4">
        <h3 className="text-lg font-semibold text-foreground">Payment Method</h3>

        <div className="grid gap-3">
          {/* Card Payment */}
          <button
            onClick={() => setSelectedMethod('card')}
            className={`p-4 rounded-lg border-2 transition-all text-left ${
              selectedMethod === 'card'
                ? 'border-maroon-800 bg-gray-50 dark:bg-gray-800'
                : 'border-gray-300 dark:border-gray-600 hover:border-maroon-600'
            }`}
          >
            <span className="text-gray-900 dark:text-white font-bold text-lg">Credit or Debit Card</span>
          </button>

          {/* Apple Pay */}
          {isApplePayAvailable ? (
            <button
              onClick={() => setSelectedMethod('apple')}
              className={`flex items-center p-4 rounded-lg border-2 transition-all ${
                selectedMethod === 'apple'
                  ? 'border-secret-primary bg-secret-primary/10'
                  : 'border-border hover:border-secret-primary/50'
              }`}
            >
              <div className="w-5 h-5 mr-3 flex items-center justify-center">
                <Smartphone size={20} className="text-maroon-800" />
              </div>
              <span className="text-foreground font-medium">Apple Pay</span>
            </button>
          ) : (
            <div className="flex items-center p-4 rounded-lg border-2 border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-800 opacity-50 cursor-not-allowed">
              <div className="w-5 h-5 mr-3 flex items-center justify-center">
                <Smartphone size={20} className="text-gray-400 dark:text-gray-600" />
              </div>
              <span className="text-gray-400 dark:text-gray-600 font-medium">Apple Pay (Unavailable)</span>
            </div>
          )}

          {/* Google Pay */}
          {isGooglePayAvailable ? (
            <button
              onClick={() => setSelectedMethod('google')}
              className={`flex items-center p-4 rounded-lg border-2 transition-all ${
                selectedMethod === 'google'
                  ? 'border-secret-primary bg-secret-primary/10'
                  : 'border-border hover:border-secret-primary/50'
              }`}
            >
              <div className="w-5 h-5 mr-3 flex items-center justify-center">
                <Smartphone size={20} className="text-maroon-800" />
              </div>
              <span className="text-foreground font-medium">Google Pay</span>
            </button>
          ) : (
            <div className="flex items-center p-4 rounded-lg border-2 border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-800 opacity-50 cursor-not-allowed">
              <div className="w-5 h-5 mr-3 flex items-center justify-center">
                <Smartphone size={20} className="text-gray-400 dark:text-gray-600" />
              </div>
              <span className="text-gray-400 dark:text-gray-600 font-medium">Google Pay (Unavailable)</span>
            </div>
          )}
        </div>
      </div>


      {/* Card Form Container */}
      {/* Always render but hide when not selected to preserve Square card element */}
      <div className={`space-y-2 mb-6 ${selectedMethod === 'card' ? '' : 'hidden'}`}>
        <h4 className="text-md font-medium text-foreground">Card Information</h4>
        <div
          id="card-container"
          className="border border-gray-300 dark:border-gray-700 rounded-lg p-4 bg-white dark:bg-gray-900 min-h-[120px]"
        />
      </div>

      {/* Terms of Sale Agreement */}
      <div className="mb-6">
        <div className="flex items-start space-x-3">
          <div className="relative flex items-center justify-center mt-0.5">
            <div
              onClick={() => setAgreedToTerms(!agreedToTerms)}
              className="w-5 h-5 border-2 rounded transition-all flex items-center justify-center cursor-pointer"
              style={{
                backgroundColor: agreedToTerms ? '#8a3134' : '#ffffff',
                borderColor: agreedToTerms ? '#8a3134' : '#d1d5db',
                borderWidth: '2px'
              }}
            >
              {agreedToTerms && (
                <svg
                  className="w-3 h-3"
                  fill="none"
                  stroke="white"
                  strokeWidth="3"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              )}
            </div>
          </div>
          <label className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed cursor-pointer"
            onClick={() => setAgreedToTerms(!agreedToTerms)}
          >
            I agree to the{' '}
            <Link
              to="/terms-of-sale"
              className="text-maroon-800 dark:text-maroon-400 hover:text-maroon-900 dark:hover:text-maroon-300 underline font-medium transition-colors"
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
            >
              Terms of Sale
            </Link>
            , including the non-refund policy.
          </label>
        </div>
      </div>

      {/* Error Display */}
      {paymentError && (
        <div className="bg-red-100 dark:bg-red-950 border-l-4 border-red-500 text-red-700 dark:text-red-300 p-4 mb-4">
          <p className="text-sm">{paymentError}</p>
        </div>
      )}

      {/* Payment Actions */}
      <div className="mt-6 space-y-3">
        <button
          onClick={handlePayment}
          disabled={isProcessing || !agreedToTerms}
          className="w-full bg-maroon-800 hover:bg-maroon-900 text-white font-bold py-4 px-4 rounded-lg shadow-lg transform hover:-translate-y-0.5 transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 border-t-4 border-maroon-900"
        >
          {isProcessing ? (
            <LoadingSpinner size="sm" />
          ) : (
            <>
              <span>
                {selectedMethod === 'apple' && 'Pay with Apple Pay'}
                {selectedMethod === 'google' && 'Pay with Google Pay'}
                {selectedMethod === 'card' && 'Complete Payment'}
              </span>
            </>
          )}
        </button>

        {/* Security Notice */}
        <div className="text-center text-xs text-muted-foreground">
          <div className="flex items-center justify-center space-x-2 mb-1">
            <div className="w-3 h-3 bg-green-500 rounded-full" />
            <span className="text-foreground">Secured by Square</span>
          </div>
          <p>Your payment information is encrypted and secure.</p>
        </div>
      </div>
    </div>
  )
}

export default PaymentForm